/**
 * 시간 문자열에서 분을 빼는 함수
 * @param timeStr - "HH:MM" 형식의 시간 문자열
 * @param minutes - 빼고 싶은 분 수
 * @returns "HH:MM" 형식의 결과 시간 문자열
 */
export function subtractMinutes(timeStr: string, minutes: number): string {
  const [hours, mins] = timeStr.split(':').map(Number);
  const totalMinutes = hours * 60 + mins - minutes;
  
  // 음수가 되면 0:00으로 설정
  if (totalMinutes < 0) {
    return '00:00';
  }
  
  const resultHours = Math.floor(totalMinutes / 60);
  const resultMins = totalMinutes % 60;
  
  return `${resultHours.toString().padStart(2, '0')}:${resultMins.toString().padStart(2, '0')}`;
}

/**
 * 시간 문자열에 분을 더하는 함수
 * @param timeStr - "HH:MM" 형식의 시간 문자열
 * @param minutes - 더하고 싶은 분 수
 * @returns "HH:MM" 형식의 결과 시간 문자열
 */
export function addMinutes(timeStr: string, minutes: number): string {
  const [hours, mins] = timeStr.split(':').map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  
  // 24시간을 넘으면 23:59로 설정
  if (totalMinutes >= 24 * 60) {
    return '23:59';
  }
  
  const resultHours = Math.floor(totalMinutes / 60);
  const resultMins = totalMinutes % 60;
  
  return `${resultHours.toString().padStart(2, '0')}:${resultMins.toString().padStart(2, '0')}`;
}

/**
 * 시간 문자열에서 시간을 빼는 함수
 * @param timeStr - "HH:MM" 형식의 시간 문자열
 * @param hours - 빼고 싶은 시간 수
 * @returns "HH:MM" 형식의 결과 시간 문자열
 */
export function subtractHours(timeStr: string, hours: number): string {
  return subtractMinutes(timeStr, hours * 60);
}

/**
 * 시간 문자열에 시간을 더하는 함수
 * @param timeStr - "HH:MM" 형식의 시간 문자열
 * @param hours - 더하고 싶은 시간 수
 * @returns "HH:MM" 형식의 결과 시간 문자열
 */
export function addHours(timeStr: string, hours: number): string {
  return addMinutes(timeStr, hours * 60);
}

/**
 * 마감할인의 valid_from을 계산합니다 (수령시작시간 30분 전)
 * @param pickupStartTime - 수령 시작 시간 ("HH:MM")
 * @param targetDate - 대상 날짜 (기본값: 오늘)
 * @returns ISO 형식의 UTC 시간
 */
export function calculateValidFrom(
  pickupStartTime: string, 
  targetDate: Date = new Date()
): Date {
  const [hours, minutes] = pickupStartTime.split(':').map(Number);
  
  // 한국 시간대 기준으로 날짜 설정
  const koreanDate = new Date(targetDate.getTime() + 9 * 60 * 60 * 1000);
  const validFromKST = new Date(
    koreanDate.getFullYear(),
    koreanDate.getMonth(),
    koreanDate.getDate(),
    hours,
    minutes - 30 // 30분 전
  );
  
  // UTC로 변환
  return new Date(validFromKST.getTime() - 9 * 60 * 60 * 1000);
}

/**
 * 주문 가능 시간을 계산합니다
 * @param pickupStartTime - 수령 시작 시간
 * @param pickupEndTime - 수령 종료 시간
 * @returns 주문 시작/종료 시간
 */
export function calculateOrderTimes(
  pickupStartTime: string,
  pickupEndTime: string
): {
  orderStartTime: string;
  orderEndTime: string;
} {
  return {
    orderStartTime: subtractMinutes(pickupStartTime, 30),
    orderEndTime: pickupEndTime
  };
}

/**
 * 시간 문자열이 유효한지 검증합니다
 * @param timeStr - "HH:MM" 형식의 시간 문자열
 * @returns 유효성 여부
 */
export function isValidTime(timeStr: string): boolean {
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  return timeRegex.test(timeStr);
}

/**
 * 시작 시간이 종료 시간보다 이른지 확인합니다
 * @param startTime - 시작 시간
 * @param endTime - 종료 시간
 * @returns 유효성 여부
 */
export function isValidTimeRange(startTime: string, endTime: string): boolean {
  if (!isValidTime(startTime) || !isValidTime(endTime)) {
    return false;
  }
  
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  
  return startMinutes < endMinutes;
}

/**
 * 30분 단위의 시간 옵션을 생성합니다
 * @param startHour - 시작 시간 (기본값: 6)
 * @param endHour - 종료 시간 (기본값: 23)
 * @returns 시간 옵션 배열
 */
export function generateTimeOptions(
  startHour: number = 6,
  endHour: number = 23
): string[] {
  const options: string[] = [];
  
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(timeStr);
    }
  }
  
  return options;
}